import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Select,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { usePatchEstimateSplit } from "../../../../views/Orders/hooks/usePatchEstimateSplit";
import { OrderPayableBy, OrderPayableTo } from "../../../../views/Orders/enums";
import { isInvalidPaymentRouteCombination } from "../utils/paymentRouteValidation";
import { extractApiErrorMessage } from "../../utils/apiErrorUtils";

export interface SplitPaymentRouteEditorProps {
  estimateNo: string;
  splitId: string;
  payableBy?: OrderPayableBy;
  payableTo?: OrderPayableTo;
  transactionType?: string;
  isExpired: boolean;
  onSaved: (estimate: unknown) => void;
}

function getSplitFromEstimate(estimate: unknown, splitId: string) {
  return (
    estimate as {
      splits?: Array<{
        _id: unknown;
        payableBy?: OrderPayableBy;
        payableTo?: OrderPayableTo;
      }>;
    }
  )?.splits?.find((s) => String(s._id) === String(splitId));
}

export const SplitPaymentRouteEditor: React.FC<SplitPaymentRouteEditorProps> = ({
  estimateNo,
  splitId,
  payableBy: payableByProp,
  payableTo: payableToProp,
  transactionType,
  isExpired,
  onSaved,
}) => {
  const toast = useToast();
  const [payableBy, setPayableBy] = useState<OrderPayableBy | "">(
    payableByProp || "",
  );
  const [payableTo, setPayableTo] = useState<OrderPayableTo | "">(
    payableToProp || "",
  );
  const [isResolvingRoute, setIsResolvingRoute] = useState(false);

  // Only sync from props when the server has saved values — avoid wiping local
  // state back to "" while a resolve-on-open request is in flight.
  useEffect(() => {
    if (payableByProp) {
      setPayableBy(payableByProp);
    }
    if (payableToProp) {
      setPayableTo(payableToProp);
    }
  }, [payableByProp, payableToProp]);

  const { mutateAsync: patchEstimateSplitAsync, isLoading: isUpdating } =
    usePatchEstimateSplit();

  const selectPayableBy = payableBy || payableByProp || "";
  const selectPayableTo = payableTo || payableToProp || "";

  const isPaymentRouteInvalid = isInvalidPaymentRouteCombination(
    selectPayableBy,
    selectPayableTo,
  );

  const applySplitPayablesToState = useCallback(
    (estimate: unknown) => {
      const updatedSplit = getSplitFromEstimate(estimate, splitId);
      if (updatedSplit?.payableBy) {
        setPayableBy(updatedSplit.payableBy);
      }
      if (updatedSplit?.payableTo) {
        setPayableTo(updatedSplit.payableTo);
      }
      return updatedSplit;
    },
    [splitId],
  );

  const handlePopoverOpen = async () => {
    if (
      !transactionType ||
      (payableByProp && payableToProp) ||
      isExpired ||
      isResolvingRoute
    ) {
      return;
    }
    if (selectPayableBy && selectPayableTo) {
      return;
    }

    setIsResolvingRoute(true);
    try {
      const estimate = await patchEstimateSplitAsync({
        estimateNo,
        splitId,
        payload: { transactionType },
      });
      const updatedSplit = applySplitPayablesToState(estimate);
      if (updatedSplit?.payableBy && updatedSplit?.payableTo) {
        onSaved(estimate);
      }
    } catch (err) {
      toast({
        title: "Failed to load payment route",
        description: extractApiErrorMessage(err, String(err)),
        status: "error",
        duration: 3000,
        position: "top",
      });
    } finally {
      setIsResolvingRoute(false);
    }
  };

  const handleSave = async () => {
    if (!transactionType) {
      toast({
        title: "Transaction type required",
        description: "Select transaction type first",
        status: "warning",
        duration: 2000,
        position: "top",
      });
      return;
    }
    if (isPaymentRouteInvalid) return;

    const effectivePayableBy = selectPayableBy;
    const effectivePayableTo = selectPayableTo;

    try {
      if (effectivePayableBy && effectivePayableTo) {
        const estimate = await patchEstimateSplitAsync({
          estimateNo,
          splitId,
          payload: {
            payableBy: effectivePayableBy,
            payableTo: effectivePayableTo,
          },
        });
        applySplitPayablesToState(estimate);
        toast({
          title: "Payment Route updated",
          status: "success",
          duration: 2000,
          position: "top",
        });
        onSaved(estimate);
        return;
      }

      const estimate = await patchEstimateSplitAsync({
        estimateNo,
        splitId,
        payload: { transactionType },
      });
      const updatedSplit = applySplitPayablesToState(estimate);
      if (!updatedSplit?.payableBy || !updatedSplit?.payableTo) {
        toast({
          title: "Selection missing",
          description:
            "Could not resolve Payable By and Payable To. Select both manually.",
          status: "warning",
          duration: 2500,
          position: "top",
        });
        return;
      }
      toast({
        title: "Payment Route updated",
        status: "success",
        duration: 2000,
        position: "top",
      });
      onSaved(estimate);
    } catch (err) {
      toast({
        title: "Failed to update Payment Route",
        description: extractApiErrorMessage(err, String(err)),
        status: "error",
        duration: 3000,
        position: "top",
      });
    }
  };

  const canEdit = Boolean(transactionType) && !isExpired;
  const isBusy = isUpdating || isResolvingRoute;

  const editIconSx = {
    fontSize: 16,
    color: canEdit ? "#805AD5" : "#9CA3AF",
  };

  if (!canEdit) {
    return (
      <EditOutlinedIcon sx={editIconSx} aria-hidden />
    );
  }

  return (
    <Popover
      placement="right"
      trigger="click"
      isLazy
      closeOnBlur
      onOpen={handlePopoverOpen}
    >
      <PopoverTrigger>
        <Box
          as="button"
          type="button"
          aria-label="Edit payment route"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          p={0.5}
          lineHeight={0}
          cursor="pointer"
          bg="transparent"
          border="none"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <EditOutlinedIcon sx={editIconSx} />
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          zIndex={1600}
          width="180px"
          boxShadow="lg"
          onClick={(e) => e.stopPropagation()}
        >
          <PopoverBody>
            <VStack spacing={2} align="stretch" mt={1}>
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="#4A5568"
                  mb={1}
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Payable By
                </Text>
                <Select
                  size="xs"
                  value={selectPayableBy}
                  onChange={(e) =>
                    setPayableBy(e.target.value as OrderPayableBy)
                  }
                  isDisabled={isBusy}
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="600"
                  color="#2D3748"
                >
                  <option value="">Select</option>
                  {Object.values(OrderPayableBy).map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="#4A5568"
                  mb={1}
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Payable To
                </Text>
                <Select
                  size="xs"
                  value={selectPayableTo}
                  onChange={(e) =>
                    setPayableTo(e.target.value as OrderPayableTo)
                  }
                  isDisabled={isBusy}
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="600"
                  color="#2D3748"
                >
                  <option value="">Select</option>
                  {Object.values(OrderPayableTo).map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </Select>
              </Box>
              {isResolvingRoute && (
                <Text
                  fontSize="xs"
                  color="gray.500"
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Loading default route…
                </Text>
              )}
              {isPaymentRouteInvalid && (
                <Text
                  fontSize="xs"
                  color="red.500"
                  fontWeight="600"
                  fontFamily="'Nunito','Inter', sans-serif"
                  lineHeight="short"
                >
                  The selected case is invalid. Please choose a valid case.
                </Text>
              )}
              <HStack spacing={2} pt={2}>
                <Button
                  size="xs"
                  variant="ghost"
                  flex="1"
                  onClick={() => {
                    setPayableBy(payableByProp || "");
                    setPayableTo(payableToProp || "");
                  }}
                  isDisabled={isBusy}
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="700"
                  color="#2D3748"
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  colorScheme="blue"
                  flex="1"
                  onClick={handleSave}
                  isLoading={isBusy}
                  isDisabled={isExpired || isPaymentRouteInvalid || isResolvingRoute}
                  fontFamily="'Nunito','Inter', sans-serif"
                  fontWeight="700"
                >
                  Save
                </Button>
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
